import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Trash2 } from 'lucide-react';
import { removeCartItem, updateCartItemQty } from '../features/cart/cartSlice';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatDisplayPrice } from '../utils/currencyDisplay';
import { Button, GlassCard } from '../components/ui';

// Helper pour générer les URL d'API
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://website-alturion-production.up.railway.app' 
    : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

const CartScreen = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;
  const { userInfo } = useSelector((state) => state.auth);

  const removeFromCartHandler = (id) => {
    dispatch(removeCartItem(id));
  };

  const checkoutHandler = async () => {
    if (!userInfo) {
      navigate('/login?redirect=cart');
      return;
    }

    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      // Vérifier s'il y a des produits gratuits
      const freeItems = cartItems.filter(item => item.price === 0);
      const paidItems = cartItems.filter(item => item.price > 0);

      if (freeItems.length > 0 && paidItems.length === 0) {
        // Tous les produits sont gratuits - rediriger vers le faux checkout
        console.log('🆓 Tous les produits sont gratuits, redirection vers checkout simulé');
        navigate('/checkout/free', { state: { cartItems } });
      } else if (freeItems.length > 0) {
        // Panier mixte - pour l'instant on refuse
        alert('Les paniers mixtes (gratuit + payant) ne sont pas encore supportés. Veuillez traiter les produits séparément.');
        return;
      } else {
        // Tous les produits sont payants, utiliser Stripe
        console.log('💳 Produits payants, utilisation de Stripe');
        const { data } = await axios.post(
          getApiUrl('/api/checkout/create-session'),
          { cartItems },
          config
        );

        if (data.sessionUrl) {
          window.location.href = data.sessionUrl;
        } else {
          console.error('URL de session Stripe manquante dans la réponse:', data);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la création de la session de paiement:', error.response?.data || error.message);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-page flex-col px-6 pt-28 pb-24 md:pt-32">
      <h1 className="mb-10 text-[28px] font-semibold tracking-tight text-body md:text-[40px]">
        {t('cart.title')}
      </h1>

      {cartItems.length === 0 ? (
        <div className="flex flex-grow items-center justify-center">
          <div className="text-center">
            <ShoppingCart className="mx-auto mb-4 h-12 w-12 text-muted" strokeWidth={1.25} />
            <p className="mb-6 text-muted">{t('cart.empty')}</p>
            <Button to="/indicators">{t('cart.backToShopping')}</Button>
          </div>
        </div>
      ) : (
        <div className="grid flex-grow grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-3 lg:col-span-2">
            {cartItems.map((item) => (
              <motion.div
                key={item.product}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <GlassCard hover={false} className="p-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-20 w-20 shrink-0 rounded-btn object-cover"
                    />
                    <div className="min-w-0 flex-grow">
                      <Link
                        to={`/product/${item.product}`}
                        className="text-base font-semibold text-body transition-colors hover:text-muted"
                      >
                        {item.name}
                      </Link>
                      <p className="mt-1 text-lg font-medium text-body">
                        {formatDisplayPrice(item.price, i18n.language)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={item.qty}
                        onChange={(e) =>
                          dispatch(
                            updateCartItemQty({
                              id: item.product,
                              qty: Number(e.target.value),
                            })
                          )
                        }
                        className="rounded-btn border border-white/10 bg-bg px-3 py-2 text-sm text-body focus:border-white/25 focus:outline-none"
                      >
                        {[...Array(item.countInStock).keys()].map((x) => (
                          <option key={x + 1} value={x + 1}>
                            {x + 1}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeFromCartHandler(item.product)}
                        className="text-muted transition-colors hover:text-danger"
                        aria-label={t('cart.remove', { defaultValue: 'Remove' })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>

          <div className="lg:col-span-1">
            <GlassCard hover={false} className="p-6 lg:sticky lg:top-24">
              <h2 className="mb-6 text-lg font-semibold text-body">{t('cart.summary')}</h2>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between text-muted">
                  <span>
                    {t('cart.items')} ({cartItems.reduce((acc, item) => acc + item.qty, 0)})
                  </span>
                  <span className="font-medium text-body">
                    {formatDisplayPrice(
                      cartItems.reduce((acc, item) => acc + item.qty * item.price, 0),
                      i18n.language
                    )}
                  </span>
                </div>
                <div className="border-t border-white/[.06] pt-4">
                  <div className="flex justify-between text-base font-semibold">
                    <span className="text-muted">{t('cart.total')}</span>
                    <span className="text-body">
                      {formatDisplayPrice(
                        cartItems.reduce((acc, item) => acc + item.qty * item.price, 0),
                        i18n.language
                      )}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={checkoutHandler}
                  className="mt-4 w-full"
                  disabled={cartItems.length === 0}
                >
                  {t('cart.checkout')}
                </Button>
              </div>
            </GlassCard>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartScreen;
